import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BsCheckLg, BsXLg, BsExclamationLg, BsInfoLg } from 'react-icons/bs';
import '../scss/premium-toast.style.scss';

export type NotifyType = 'success' | 'error' | 'warning' | 'info';

const ICONS: Record<NotifyType, React.ReactNode> = {
    success: <BsCheckLg />,
    error: <BsXLg />,
    warning: <BsExclamationLg />,
    info: <BsInfoLg />,
};

interface AlertItem {
    id: number;
    type: NotifyType;
    title: React.ReactNode;
    message?: React.ReactNode;
    okLabel: string;
    onOk?: () => void;
    /** optional primary call-to-action (e.g. "Download certificate") */
    actionLabel?: string;
    onAction?: () => void | Promise<void>;
    /** label shown on the action button while an async onAction runs */
    actionBusyLabel?: string;
    closing?: boolean;
}

/* ---- tiny external store so notify() can be called imperatively anywhere ---- */
let alerts: AlertItem[] = [];
let listeners: Array<(a: AlertItem[]) => void> = [];
let seq = 0;
let hostMounted = false;

const emit = () => listeners.forEach((l) => l([...alerts]));

const removeAlert = (id: number) => {
    // mark closing → play exit animation → remove
    alerts = alerts.map((a) => (a.id === id ? { ...a, closing: true } : a));
    emit();
    window.setTimeout(() => {
        alerts = alerts.filter((a) => a.id !== id);
        emit();
    }, 260);
};

const AlertCard: React.FC<{ alert: AlertItem; isTop: boolean }> = ({ alert, isTop }) => {
    const okRef = useRef<HTMLButtonElement>(null);
    const actionRef = useRef<HTMLButtonElement>(null);
    const [busy, setBusy] = useState(false);
    const hasAction = !!alert.actionLabel && !!alert.onAction;

    const close = () => {
        alert.onOk?.();
        removeAlert(alert.id);
    };

    /* Run the optional CTA, then always dismiss. `onAction` owns its own
       error handling — anything it queues (e.g. a follow-up error alert)
       surfaces once this card finishes closing, since the host renders
       one alert at a time. */
    const runAction = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await alert.onAction?.();
        } finally {
            setBusy(false);
            removeAlert(alert.id);
        }
    };

    useEffect(() => {
        if (!isTop) return;
        // Focus the CTA when there is one, otherwise the OK button.
        const focusTimer = window.setTimeout(
            () => (hasAction ? actionRef.current : okRef.current)?.focus(),
            120
        );
        const onKey = (e: KeyboardEvent) => {
            /* With a CTA present, let Enter activate whichever button has
               focus natively — swallowing it here would make Enter always
               mean "dismiss" and put the CTA out of keyboard reach. */
            if (e.key === 'Escape' || (e.key === 'Enter' && !hasAction)) {
                e.preventDefault();
                close();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => {
            window.clearTimeout(focusTimer);
            document.removeEventListener('keydown', onKey);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTop, hasAction]);

    return (
        <div
            className={`pt-alert pt-${alert.type}${alert.closing ? ' is-closing' : ''}`}
            role="alertdialog"
            aria-modal="true"
        >
            <span className="pt-alert__icon" aria-hidden="true">{ICONS[alert.type]}</span>
            <div className="pt-alert__title">{alert.title}</div>
            {alert.message ? <div className="pt-alert__msg">{alert.message}</div> : null}
            {hasAction ? (
                <div className="pt-alert__actions">
                    <button
                        ref={actionRef}
                        type="button"
                        className="pt-alert__ok"
                        onClick={runAction}
                        disabled={busy}
                    >
                        {busy ? (alert.actionBusyLabel ?? alert.actionLabel) : alert.actionLabel}
                    </button>
                    <button
                        ref={okRef}
                        type="button"
                        className="pt-alert__ghost"
                        onClick={close}
                        disabled={busy}
                    >
                        {alert.okLabel}
                    </button>
                </div>
            ) : (
                <button ref={okRef} type="button" className="pt-alert__ok" onClick={close}>
                    {alert.okLabel}
                </button>
            )}
        </div>
    );
};

const AlertHost: React.FC = () => {
    const [items, setItems] = useState<AlertItem[]>(alerts);
    useEffect(() => {
        listeners.push(setItems);
        return () => { listeners = listeners.filter((l) => l !== setItems); };
    }, []);

    if (!items.length) return null;
    // Show ONE alert at a time (FIFO queue): render only the first alert in
    // line. The next queued alert appears after the current one is dismissed,
    // so multiple alerts fired together play sequentially instead of stacking
    // on top of each other.
    const current = items[0];
    const overlayClosing = items.length === 1 && !!current.closing; // last one leaving → fade backdrop

    return (
        <div className={`pt-overlay${overlayClosing ? ' is-closing' : ''}`}>
            <div className="pt-overlay__stack">
                <AlertCard key={current.id} alert={current} isTop={true} />
            </div>
        </div>
    );
};

const mountHost = () => {
    if (hostMounted || typeof document === 'undefined') return;
    hostMounted = true;
    const el = document.createElement('div');
    el.id = 'pt-alert-root';
    document.body.appendChild(el);
    createRoot(el).render(<AlertHost />);
};

interface NotifyArgs {
    type?: NotifyType;
    title: React.ReactNode;
    message?: React.ReactNode;
    /** confirm button label (default "OK") */
    okLabel?: string;
    /** optional callback fired when the user clicks OK */
    onOk?: () => void;
    /**
     * Optional primary call-to-action. When supplied the card shows two
     * buttons: this one (gradient, focused by default) plus `okLabel` as a
     * quiet secondary. The alert dismisses once `onAction` settles, so an
     * async handler keeps the card up — showing `actionBusyLabel` — while it
     * runs.
     */
    actionLabel?: string;
    onAction?: () => void | Promise<void>;
    actionBusyLabel?: string;
}

/**
 * Premium centered alert — glass backdrop + a clean card with a gradient icon,
 * title, optional message and an OK button. Does NOT auto-dismiss; the user
 * clicks OK (or presses Enter/Esc). Animated open + close. Works on any page —
 * self-mounts to <body>. Variants: success | error | warning | info.
 */
function notifyBase({
    type = 'info',
    title,
    message,
    okLabel = 'OK',
    onOk,
    actionLabel,
    onAction,
    actionBusyLabel,
}: NotifyArgs) {
    mountHost();
    // De-dupe: if an identical alert is already queued/open (same type + title +
    // message), don't stack a second copy. Prevents the same API error showing
    // twice when two requests fail with the same message.
    const duplicate = alerts.find(
        (a) => !a.closing && a.type === type && a.title === title && a.message === message
    );
    if (duplicate) return duplicate.id;
    const id = ++seq;
    alerts = [
        ...alerts,
        { id, type, title, message, okLabel, onOk, actionLabel, onAction, actionBusyLabel },
    ];
    emit();
    return id;
}

type QuickOptions = Omit<NotifyArgs, 'type' | 'title' | 'message'>;

/** shorthand builder: notify.success(title, message?, opts?) */
const quick = (type: NotifyType) =>
    (title: React.ReactNode, message?: React.ReactNode, opts?: QuickOptions) =>
        notifyBase({ type, title, message, ...opts });

/** Close every open alert (plays the exit animation first). */
const dismissAll = () => {
    alerts = alerts.map((a) => ({ ...a, closing: true }));
    emit();
    window.setTimeout(() => { alerts = []; emit(); }, 260);
};

/**
 * THE shared alert component for the whole app. Import it anywhere and call:
 *   notify.success('Saved successfully', 'Your changes have been applied.');
 *   notify.error('Something went wrong', 'Please try again.');
 *   notify.warning('Heads up', 'This action cannot be undone.');
 *   notify.info('Did you know?', 'You can search FAQs by keyword.');
 * Or the object form for full control (okLabel / onOk / a primary action):
 *   notify({ type: 'success', title: '...', message: '...', onOk: () => {} });
 *   notify({ type: 'success', title: 'Your file is ready!',
 *            actionLabel: 'Download', onAction: () => downloadIt(),
 *            actionBusyLabel: 'Downloading...', okLabel: 'Maybe later' });
 */
export const notify = Object.assign(notifyBase, {
    success: quick('success'),
    error: quick('error'),
    warning: quick('warning'),
    info: quick('info'),
    dismissAll,
});

export default notify;
